/**
 * The LIBRARY interior (plot P7, domain "knowledge").
 *
 * Walking in, the player sees what the org KNOWS: every knowledge subject is a
 * book on a shelf, spine colour hashed from its entity_kind, and every
 * relationship between two knowledge entries is a physical copper thread
 * running spine-to-spine. A map table in the entry commons carries the same
 * graph as pins + threads — the overview the stacks themselves can't give.
 *
 * The room lives on its own coordinate ISLAND at LIBRARY_ISLAND_Z (see
 * interiors.ts), authored entirely in island-LOCAL coordinates and parented to
 * one root entity the caller positions — exactly like the office. Everything
 * dynamic (books, threads, pins) hangs off a `contentRoot` child so it can be
 * rebuilt when the knowledge set changes without touching the shell.
 *
 * An empty authorized knowledge collection remains a valid empty room. The
 * session-owned remote-state controller handles refresh and recovery.
 *
 * This module keeps its geometry math in exported PURE helpers (stackCenterX,
 * bookSlot, bookPosition, shelveBooks, spineColor, graphEdges, edgeOpacity,
 * mapPinPositions) so the layout is unit-testable without the engine.
 */
import * as pc from 'playcanvas'
import { useArenaStore } from '../../store'
import type { AABB } from '../colliders'
import { INPUT_PRIORITY, modalInputRouter } from '../../input/modalInputRouter'
import { LIBRARY_ISLAND_Z } from './interiors'
import { computeLabelScreenPos } from './labels'
import { injectWorldCardStyles } from './labelStyles'
import type { KnowledgeEntry } from '../../api/types'
import {
  createRenderEntity,
  createWorldOverlay,
  escapeWorldHtml,
  type OverlayHandle,
} from './interiorShared'
import { createInteriorMaterial, InteriorDressingKit } from './interiorDressing'

import {
  LIBRARY_HALF_X,
  LIBRARY_HALF_Z,
  LIBRARY_CEILING,
  WALL_THICKNESS,
  ENTRY_DOOR_HALF_WIDTH,
  ENTRY_DOOR_HEIGHT,
  LIBRARY_STACK_COUNT,
  LIBRARY_SHELF_YS,
  RUN_DEPTH_X,
  RUN_MIN_Z,
  RUN_MAX_Z,
  STACK_HEIGHT,
  LIBRARY_STACK_COLLIDERS,
  BOOK_WIDTH_Z,
  BOOK_HEIGHT,
  BOOK_DEPTH_X,
  stackCenterX,
  BookPlacement,
  shelveBooks,
  spineColor,
  graphEdges,
  renderedGraphEdges,
  edgeOpacity,
  MAP_TABLE,
  LIBRARY_MAP_COLLIDER,
  MAX_LIBRARY_RENDERED_SUBJECTS,
  mapPinPositions,
  MAX_LABELLED_BOOKS,
  LABEL_ATLAS_COLS,
  LABEL_CELL_W,
  LABEL_CELL_H,
  INTERACT_DIST,
} from './libraryLayout'

// Re-export the pure layout so consumers can import it from either module.
export * from './libraryLayout'

type RemoteProjectionStatus = 'disconnected' | 'loading' | 'fresh' | 'stale'

export function libraryProjectionState(
  status: RemoteProjectionStatus,
  entryCount: number,
): { badge: string | null; confirmedEmpty: boolean } {
  if (status === 'fresh') {
    return { badge: null, confirmedEmpty: entryCount === 0 }
  }
  if (status === 'loading') {
    return { badge: 'PORTAL PROJECTION LOADING', confirmedEmpty: false }
  }
  if (status === 'stale') {
    return { badge: 'PORTAL PROJECTION STALE', confirmedEmpty: false }
  }
  return { badge: 'PORTAL DISCONNECTED', confirmedEmpty: false }
}

export interface LibraryIndexState {
  heading: 'KNOWLEDGE INDEX'
  detail: string
  degraded: boolean
  state: 'fresh-empty' | 'fresh-populated' | 'loading' | 'stale' | 'disconnected'
}

/** Truthful in-world index copy derived only from the authorized projection. */
export function libraryIndexState(
  status: RemoteProjectionStatus,
  entries: readonly KnowledgeEntry[],
): LibraryIndexState {
  const projection = libraryProjectionState(status, entries.length)
  if (projection.badge) {
    const projectionState = status === 'loading'
      ? 'loading'
      : status === 'stale'
        ? 'stale'
        : 'disconnected'
    return {
      heading: 'KNOWLEDGE INDEX',
      detail: projection.badge,
      degraded: true,
      state: projectionState,
    }
  }
  if (projection.confirmedEmpty) {
    return {
      heading: 'KNOWLEDGE INDEX',
      detail: '0 AUTHORIZED SUBJECTS',
      degraded: false,
      state: 'fresh-empty',
    }
  }
  const relationships = graphEdges(entries).length
  return {
    heading: 'KNOWLEDGE INDEX',
    detail: entries.length > MAX_LIBRARY_RENDERED_SUBJECTS
      ? `${entries.length} SUBJECTS · ${MAX_LIBRARY_RENDERED_SUBJECTS} SHOWN`
      : `${entries.length} SUBJECT${entries.length === 1 ? '' : 'S'} · ${relationships} RELATIONSHIP${relationships === 1 ? '' : 'S'}`,
    degraded: false,
    state: 'fresh-populated',
  }
}

interface BookEntity {
  placement: BookPlacement
  entity: pc.Entity
  /** World-space centre (island offset already applied), for proximity. */
  worldX: number
  worldY: number
  worldZ: number
  labelCell: number
  labelPlane: pc.Entity | null
}

interface ThreadEntity {
  entity: pc.Entity
  material: pc.StandardMaterial
  fromId: string
  toId: string
  baseOpacity: number
}

export class LibrarySystem {
  private app!: pc.Application
  private root!: pc.Entity
  private cameraEntity!: pc.Entity
  private contentRoot!: pc.Entity

  private books: BookEntity[] = []
  private threads: ThreadEntity[] = []
  /** Map-table pins and other dynamic decoration (destroyed on rebuild). */
  private extras: pc.Entity[] = []
  private labelAtlas: pc.Texture | null = null
  private labelMats = new Map<number, pc.StandardMaterial>()

  private entries: KnowledgeEntry[] = []
  private emptyLogged = false
  private knowledgeError: string | null = null
  private active = false
  private unsubscribe?: () => void
  private disposeInput?: () => void

  private container!: HTMLDivElement
  private overlay: OverlayHandle | null = null
  private card!: HTMLDivElement
  private statusBadge!: HTMLDivElement
  private indexStatusMaterial: pc.StandardMaterial | null = null
  private indexStatusTexture: pc.Texture | null = null
  private indexStatusEntity: pc.Entity | null = null
  private selectedId: string | null = null
  private currentInteractId: string | null = null

  private readonly onKeyDown = (e: KeyboardEvent): boolean => {
    if (e.code !== 'KeyE') return false
    if (!this.currentInteractId) return false
    this.select(this.currentInteractId)
    return true
  }

  init(app: pc.Application, root: pc.Entity, cameraEntity: pc.Entity): void {
    this.app = app
    this.root = root
    this.cameraEntity = cameraEntity

    this.contentRoot = new pc.Entity('library-content')
    root.addChild(this.contentRoot)

    this.buildShell()
    this.buildStacks()
    this.buildMapTable()
    const staticBatch = app.batcher?.addGroup('library-static', false, 64)
    if (staticBatch) {
      for (const render of root.findComponents('render') as pc.RenderComponent[]) {
        render.batchGroupId = staticBatch.id
      }
    }

    // DOM overlay for the [E] prompt + book detail card.
    injectWorldCardStyles()
    this.overlay = createWorldOverlay(app)
    this.container = this.overlay.element

    this.card = document.createElement('div')
    Object.assign(this.card.style, {
      position: 'absolute',
      fontFamily: 'ui-monospace, monospace',
      fontSize: '12px',
      lineHeight: '1.4',
      whiteSpace: 'nowrap',
      userSelect: 'none',
      display: 'none',
    })
    this.container.appendChild(this.card)

    this.statusBadge = document.createElement('div')
    Object.assign(this.statusBadge.style, {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      padding: '0.5rem 0.75rem',
      border: '1px solid rgba(251,191,36,0.7)',
      borderRadius: '0.5rem',
      background: 'rgba(30,20,10,0.92)',
      color: '#fbbf24',
      fontFamily: 'ui-monospace, monospace',
      fontSize: '12px',
      letterSpacing: '0.04em',
      display: 'none',
    })
    this.container.appendChild(this.statusBadge)

    this.unsubscribe = useArenaStore.subscribe((state, prev) => {
      if (
        state.knowledge !== prev.knowledge ||
        state.remoteProjectionStatus !== prev.remoteProjectionStatus
      ) this.syncKnowledge(state.knowledge, state.remoteProjectionStatus)
    })
    const initial = useArenaStore.getState()
    this.syncKnowledge(initial.knowledge, initial.remoteProjectionStatus)
    this.disposeInput = modalInputRouter.register(INPUT_PRIORITY.room, this.onKeyDown)
  }

  // ── Per-frame; gated on the player being inside P7 ────────────────────────

  frame(active: boolean): void {
    this.active = active
    this.syncStatusBadge()
    if (!active) {
      // Not in the library — keep the prompt hidden and don't poll.
      if (this.currentInteractId !== null || this.card.style.display !== 'none') {
        this.currentInteractId = null
        this.card.style.display = 'none'
      }
      return
    }

    this.updateProximity()
  }

  private syncKnowledge(
    entries: KnowledgeEntry[],
    status: RemoteProjectionStatus,
  ): void {
    const projection = libraryProjectionState(status, entries.length)
    this.knowledgeError = projection.badge
    this.paintIndexStatus(libraryIndexState(status, entries))
    if (!this.sameEntries(entries)) {
      this.entries = entries
      this.rebuildContent()
    }
    if (projection.confirmedEmpty && !this.emptyLogged) {
      this.emptyLogged = true
      console.info('[library] no authorized knowledge subjects — showing an empty library')
    }
  }

  private syncStatusBadge(): void {
    if (!this.statusBadge) return
    const visible = this.active && this.knowledgeError !== null
    this.statusBadge.style.display = visible ? 'block' : 'none'
    if (visible) {
      this.statusBadge.textContent = `KNOWLEDGE DEGRADED · ${this.knowledgeError}`
    }
  }

  private sameEntries(next: readonly KnowledgeEntry[]): boolean {
    if (next.length !== this.entries.length) return false
    for (let i = 0; i < next.length; i++) {
      if (next[i].id !== this.entries[i].id) return false
      if (next[i].title !== this.entries[i].title) return false
      if (next[i].entityKind !== this.entries[i].entityKind) return false
      if (next[i].relationships.length !== this.entries[i].relationships.length) return false
      for (let relationship = 0; relationship < next[i].relationships.length; relationship++) {
        const current = this.entries[i].relationships[relationship]
        const candidate = next[i].relationships[relationship]
        if (
          candidate.to_id !== current.to_id ||
          candidate.type !== current.type ||
          candidate.confidence !== current.confidence
        ) return false
      }
    }
    return true
  }

  // ── Static shell ──────────────────────────────────────────────────────────

  private addBox(
    name: string,
    type: 'box' | 'plane',
    pos: [number, number, number],
    scale: [number, number, number],
    mat: pc.Material,
    parent?: pc.Entity,
    collider?: AABB,
  ): pc.Entity {
    return createRenderEntity({
      name,
      type,
      position: pos,
      scale,
      material: mat,
      parent: parent ?? this.root,
      collider,
    })
  }

  private buildShell(): void {
    const H = LIBRARY_CEILING

    // Floor — deep slate with enough reflected light to keep the room legible.
    const floorMat = new pc.StandardMaterial()
    floorMat.diffuse = new pc.Color(0.16, 0.19, 0.22)
    floorMat.gloss = 0.4
    floorMat.update()
    this.addBox('lib-floor', 'plane', [0, 0, 0], [LIBRARY_HALF_X * 2, 1, LIBRARY_HALF_Z * 2], floorMat,
      this.root,
      { minX: -LIBRARY_HALF_X, maxX: LIBRARY_HALF_X, minY: -1, maxY: 0, minZ: -LIBRARY_HALF_Z, maxZ: LIBRARY_HALF_Z })

    // Walls — quiet blue-grey plaster against the graphite shelving.
    const wallMat = new pc.StandardMaterial()
    wallMat.diffuse = new pc.Color(0.40, 0.42, 0.43)
    wallMat.gloss = 0.1
    wallMat.update()

    const accentMat = new pc.StandardMaterial()
    accentMat.diffuse = new pc.Color(0.16, 0.23, 0.28)
    accentMat.gloss = 0.25
    accentMat.update()

    const wall = (name: string, pos: [number, number, number], scale: [number, number, number]): void => {
      this.addBox(name, 'box', pos, scale, wallMat, this.root, {
        minX: pos[0] - scale[0] / 2, maxX: pos[0] + scale[0] / 2,
        minY: pos[1] - scale[1] / 2, maxY: pos[1] + scale[1] / 2,
        minZ: pos[2] - scale[2] / 2, maxZ: pos[2] + scale[2] / 2,
      })
    }

    // North, west, east — solid.
    wall('lib-wall-n', [0, H / 2, -LIBRARY_HALF_Z], [LIBRARY_HALF_X * 2, H, WALL_THICKNESS])
    wall('lib-wall-w', [-LIBRARY_HALF_X, H / 2, 0], [WALL_THICKNESS, H, LIBRARY_HALF_Z * 2])
    wall('lib-wall-e', [LIBRARY_HALF_X, H / 2, 0], [WALL_THICKNESS, H, LIBRARY_HALF_Z * 2])

    // South wall — split around the entry gap, with a lintel over the door.
    const southZ = LIBRARY_HALF_Z
    const flankW = LIBRARY_HALF_X - ENTRY_DOOR_HALF_WIDTH
    for (const s of [-1, 1] as const) {
      const cx = s * (ENTRY_DOOR_HALF_WIDTH + flankW / 2)
      wall(`lib-wall-s-${s}`, [cx, H / 2, southZ], [flankW, H, WALL_THICKNESS])
    }
    // Lintel above the door.
    wall('lib-wall-s-lintel', [0, ENTRY_DOOR_HEIGHT + (H - ENTRY_DOOR_HEIGHT) / 2, southZ],
      [ENTRY_DOOR_HALF_WIDTH * 2, H - ENTRY_DOOR_HEIGHT, WALL_THICKNESS])

    // Architectural backdrops visually bind the three programs. They are
    // shallow finish panels, not new collision or room ownership.
    this.addBox('lib-reading-backdrop-west', 'box',
      [-6.2, 1.55, southZ - 0.19], [4.8, 2.8, 0.06], accentMat, this.root)
    this.addBox('lib-reading-backdrop-east', 'box',
      [6.2, 1.55, southZ - 0.19], [4.8, 2.8, 0.06], accentMat, this.root)
    this.addBox('lib-research-backdrop', 'box',
      [0, 1.7, -LIBRARY_HALF_Z + 0.19], [16, 3.2, 0.06], accentMat, this.root)

    // Ceiling — double-sided so you don't see through it from below.
    const ceilMat = new pc.StandardMaterial()
    ceilMat.diffuse = new pc.Color(0.13, 0.16, 0.20)
    ceilMat.cull = pc.CULLFACE_NONE
    ceilMat.update()
    this.addBox('lib-ceiling', 'plane', [0, H, 0], [LIBRARY_HALF_X * 2, 1, LIBRARY_HALF_Z * 2], ceilMat,
      this.root,
      { minX: -LIBRARY_HALF_X, maxX: LIBRARY_HALF_X, minY: H, maxY: H + 1, minZ: -LIBRARY_HALF_Z, maxZ: LIBRARY_HALF_Z })

    // Dark backdrop shell just outside — closes every sightline through the
    // door onto the bare island, without modelling a street.
    const backMat = new pc.StandardMaterial()
    backMat.diffuse = new pc.Color(0.04, 0.04, 0.05)
    backMat.useLighting = false
    backMat.cull = pc.CULLFACE_NONE
    backMat.update()
    const pad = 6
    const bh = H + 6
    const bx = LIBRARY_HALF_X + pad
    const bz = LIBRARY_HALF_Z + pad
    this.addBox('lib-back-n', 'box', [0, bh / 2, -bz], [bx * 2, bh, 0.4], backMat, this.root)
    this.addBox('lib-back-s', 'box', [0, bh / 2, bz], [bx * 2, bh, 0.4], backMat, this.root)
    this.addBox('lib-back-w', 'box', [-bx, bh / 2, 0], [0.4, bh, bz * 2], backMat, this.root)
    this.addBox('lib-back-e', 'box', [bx, bh / 2, 0], [0.4, bh, bz * 2], backMat, this.root)
    this.addBox('lib-back-lid', 'plane', [0, bh, 0], [bx * 2, 1, bz * 2], backMat, this.root)
    this.addBox('lib-back-floor', 'plane', [0, -0.05, 0], [bx * 2, 1, bz * 2], backMat, this.root)

    // ── Way out — lit frame + EXIT sign over the south door ─────────────────
    const frameMat = new pc.StandardMaterial()
    frameMat.diffuse = new pc.Color(0.1, 0.11, 0.13)
    frameMat.useMetalness = true
    frameMat.metalness = 0.5
    frameMat.gloss = 0.5
    frameMat.update()

    const glowMat = new pc.StandardMaterial()
    glowMat.diffuse = new pc.Color(0.1, 0.85, 0.45)
    glowMat.emissive = new pc.Color(0.2, 1.0, 0.55)
    glowMat.emissiveIntensity = 14
    glowMat.useLighting = false
    glowMat.update()

    const exitZ = southZ - 0.1
    const jamb = 0.16
    for (const side of [-1, 1] as const) {
      this.addBox(`lib-exit-jamb-${side}`, 'box',
        [side * (ENTRY_DOOR_HALF_WIDTH + jamb / 2), ENTRY_DOOR_HEIGHT / 2, exitZ],
        [jamb, ENTRY_DOOR_HEIGHT, 0.2], frameMat, this.root)
    }
    this.addBox('lib-exit-head', 'box',
      [0, ENTRY_DOOR_HEIGHT + jamb / 2, exitZ],
      [ENTRY_DOOR_HALF_WIDTH * 2 + jamb * 2, jamb, 0.2], frameMat, this.root)
    this.addBox('lib-exit-sign', 'box',
      [0, ENTRY_DOOR_HEIGHT + 0.42, exitZ - 0.06],
      [0.9, 0.26, 0.06], glowMat, this.root)

    // ── Lighting ────────────────────────────────────────────────────────────
    this.app.scene.ambientLight = new pc.Color(0.36, 0.36, 0.35)
    const fixtureMat = new pc.StandardMaterial()
    fixtureMat.diffuse = new pc.Color(1.0, 0.88, 0.70)
    fixtureMat.emissive = new pc.Color(1.0, 0.72, 0.42)
    fixtureMat.emissiveIntensity = 2.2
    fixtureMat.useLighting = false
    fixtureMat.update()
    // Emissive pendant fixture per aisle gives the ceiling a readable rhythm.
    // One broad key actually lights them all: a point light per aisle multiplied
    // every book/shelf pass and pushed the populated room above 380 draw calls.
    for (let i = 0; i < LIBRARY_STACK_COUNT - 1; i++) {
      const x = (stackCenterX(i) + stackCenterX(i + 1)) / 2
      this.addBox(`lib-fixture-${i}`, 'box', [x, H - 0.12, 0], [1.25, 0.08, 2.8], fixtureMat, this.root)
    }
    const keyLight = new pc.Entity('lib-light-key')
    keyLight.addComponent('light', {
      type: 'point',
      color: new pc.Color(1.0, 0.91, 0.78),
      intensity: 3.35,
      range: 40,
      castShadows: false,
    })
    keyLight.setLocalPosition(0, H - 0.5, 1)
    this.root.addChild(keyLight)
  }

  private buildStacks(): void {
    // Structural carcass per run (always visible + collidable). Shelf ledges
    // and projected books provide the detail. The former four multi-mesh GLBs
    // per run added hundreds of draw calls while sitting directly behind the
    // same carcass, so they were visual duplication rather than useful detail.
    const carcassMat = new pc.StandardMaterial()
    carcassMat.diffuse = new pc.Color(0.16, 0.19, 0.22)
    carcassMat.gloss = 0.3
    carcassMat.update()

    const ledgeMat = new pc.StandardMaterial()
    ledgeMat.diffuse = new pc.Color(0.48, 0.34, 0.18)
    ledgeMat.gloss = 0.25
    ledgeMat.update()

    const runLen = RUN_MAX_Z - RUN_MIN_Z
    const runCz = (RUN_MIN_Z + RUN_MAX_Z) / 2

    for (let i = 0; i < LIBRARY_STACK_COUNT; i++) {
      const cx = stackCenterX(i)
      const collider = LIBRARY_STACK_COLLIDERS[i]
      // Carcass box + collider so aisles are walls, not walk-through.
      this.addBox(`lib-stack-${i}`, 'box', [cx, STACK_HEIGHT / 2, runCz],
        [RUN_DEPTH_X, STACK_HEIGHT, runLen], carcassMat, this.root, collider)

      // Shelf ledges on both faces.
      for (const shelfY of LIBRARY_SHELF_YS) {
        for (const s of [-1, 1] as const) {
          this.addBox(`lib-ledge-${i}-${shelfY}-${s}`, 'box',
            [cx + s * (RUN_DEPTH_X / 2 + BOOK_DEPTH_X / 2), shelfY - 0.03, runCz],
            [BOOK_DEPTH_X + 0.06, 0.04, runLen], ledgeMat, this.root)
        }
      }
    }
  }

  private buildMapTable(): void {
    const legMat = new pc.StandardMaterial()
    legMat.diffuse = new pc.Color(0.24, 0.17, 0.10)
    legMat.update()
    const topMat = new pc.StandardMaterial()
    topMat.diffuse = new pc.Color(0.09, 0.14, 0.18)
    topMat.emissive = new pc.Color(0.05, 0.15, 0.20)
    topMat.emissiveIntensity = 0.75
    topMat.gloss = 0.5
    topMat.update()

    const { x, z, top, halfX, halfZ } = MAP_TABLE
    const dressing = new InteriorDressingKit(this.root)
    const frame = createInteriorMaterial([0.18, 0.22, 0.26], { metalness: 0.35, gloss: 0.45 })
    const glow = createInteriorMaterial([0.34, 0.76, 0.92], { emissive: 1.7 })
    const warmGlow = createInteriorMaterial([0.92, 0.60, 0.30], { emissive: 1.4 })
    const rug = createInteriorMaterial([0.16, 0.30, 0.34])
    const readingZone = createInteriorMaterial([0.13, 0.25, 0.28])
    const researchZone = createInteriorMaterial([0.20, 0.25, 0.28])
    this.addBox('lib-commons-rug', 'box', [x, 0.018, z], [halfX * 2 + 2.8, 0.036, halfZ * 2 + 2.2], rug, this.root)
    this.addBox('lib-reading-zone-west', 'box',
      [-6.2, 0.019, 10.25], [4.3, 0.038, 3.2], readingZone, this.root)
    this.addBox('lib-reading-zone-east', 'box',
      [6.2, 0.019, 10.25], [4.3, 0.038, 3.2], readingZone, this.root)
    this.addBox('lib-research-zone', 'box',
      [0, 0.019, -10.35], [12.0, 0.038, 4.4], researchZone, this.root)
    // Top surface + a shallow rim, collidable so you can't walk through it.
    this.addBox('lib-table-top', 'box', [x, top - 0.05, z], [halfX * 2, 0.1, halfZ * 2], topMat, this.root, {
      ...LIBRARY_MAP_COLLIDER,
    })
    for (const sx of [-1, 1] as const) {
      for (const sz of [-1, 1] as const) {
        this.addBox(`lib-table-leg-${sx}-${sz}`, 'box',
          [x + sx * (halfX - 0.4), (top - 0.1) / 2, z + sz * (halfZ - 0.3)],
          [0.12, top - 0.1, 0.12], legMat, this.root)
      }
    }
    dressing.addCeilingRaft('lib-commons-ceiling', x, z, 6.8, 2.8, LIBRARY_CEILING, frame, glow)
    dressing.addCeilingRaft('lib-research-ceiling', 0, -10.35, 9.0, 2.8, LIBRARY_CEILING, frame, warmGlow)
    this.addBox('lib-index-light', 'box', [x, top + 0.06, z], [2.2, 0.04, 0.10], glow, this.root)

    // A physical, in-world source-status surface. It never fabricates
    // knowledge: fresh empty, populated, and degraded projections each render
    // their own explicit state.
    const indexMat = new pc.StandardMaterial()
    indexMat.diffuse = new pc.Color(0.05, 0.09, 0.12)
    indexMat.emissive = new pc.Color(0.34, 0.76, 0.92)
    indexMat.emissiveIntensity = 0.75
    indexMat.useLighting = false
    indexMat.update()
    this.indexStatusMaterial = indexMat
    this.indexStatusEntity = this.addBox(
      'lib-index-status',
      'box',
      [0, 2.82, 6.72],
      [4.0, 0.72, 0.06],
      indexMat,
      this.root,
    )

    const researchLight = new pc.Entity('lib-light-research')
    researchLight.addComponent('light', {
      type: 'point',
      color: new pc.Color(1.0, 0.76, 0.52),
      intensity: 1.5,
      range: 12,
      castShadows: false,
    })
    researchLight.setLocalPosition(0, LIBRARY_CEILING - 0.55, -10.35)
    this.root.addChild(researchLight)
  }

  private paintIndexStatus(state: LibraryIndexState): void {
    const material = this.indexStatusMaterial
    if (!material) return
    const statusTags = [
      'library-index-fresh-empty',
      'library-index-fresh-populated',
      'library-index-loading',
      'library-index-stale',
      'library-index-disconnected',
    ]
    this.indexStatusEntity?.tags.remove(...statusTags)
    this.indexStatusEntity?.tags.add(`library-index-${state.state}`)
    try {
      const canvas = document.createElement('canvas')
      canvas.width = 768
      canvas.height = 192
      const context = canvas.getContext('2d')
      if (!context) return
      context.fillStyle = '#0b151d'
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.fillStyle = state.degraded ? '#f0a24b' : '#55c6e8'
      context.fillRect(0, 0, 16, canvas.height)
      context.fillStyle = '#eef6f8'
      context.font = '700 50px Nunito, system-ui, sans-serif'
      context.fillText(state.heading, 52, 78)
      context.fillStyle = state.degraded ? '#f4c081' : '#9fd7e5'
      context.font = '600 30px ui-monospace, monospace'
      context.fillText(state.detail, 52, 136)
      const texture = new pc.Texture(this.app.graphicsDevice, {
        width: canvas.width,
        height: canvas.height,
      })
      texture.setSource(canvas)
      const previous = this.indexStatusTexture
      this.indexStatusTexture = texture
      material.diffuseMap = texture
      material.emissiveMap = texture
      material.emissive = new pc.Color(1, 1, 1)
      material.emissiveIntensity = 0.85
      material.update()
      previous?.destroy()
    } catch {
      // The physical cyan/amber panel remains truthful even if a headless
      // canvas implementation cannot provide the text texture.
    }
  }

  // ── Dynamic content (books, threads, pins) ────────────────────────────────

  private rebuildContent(): void {
    this.clearContent()
    if (this.entries.length === 0) return

    const placements = shelveBooks(
      this.entries.slice(0, MAX_LIBRARY_RENDERED_SUBJECTS),
    )
    const placedIds = new Set(placements.map((p) => p.id))

    this.buildLabelAtlas(placements)
    this.buildBooks(placements)
    this.buildThreads(placements)
    this.buildMapGraph(placedIds)
  }

  private buildBooks(placements: readonly BookPlacement[]): void {
    // One material per entity kind (cheap; kinds are few).
    const kindMats = new Map<string, pc.StandardMaterial>()
    const matFor = (kind: string): pc.StandardMaterial => {
      const cached = kindMats.get(kind)
      if (cached) return cached
      const [r, g, b] = spineColor(kind)
      const mat = new pc.StandardMaterial()
      mat.diffuse = new pc.Color(r, g, b)
      mat.gloss = 0.35
      mat.update()
      kindMats.set(kind, mat)
      return mat
    }

    placements.forEach((p, i) => {
      const e = new pc.Entity(`lib-book-${p.id}`)
      e.addComponent('render', { type: 'box' })
      e.setLocalPosition(p.x, p.y, p.z)
      e.setLocalScale(BOOK_DEPTH_X, BOOK_HEIGHT, BOOK_WIDTH_Z)
      const mi = e.render?.meshInstances[0]
      if (mi) mi.material = matFor(p.entityKind)
      this.contentRoot.addChild(e)
      this.books.push({
        placement: p,
        entity: e,
        worldX: p.x,
        worldY: p.y,
        worldZ: p.z + LIBRARY_ISLAND_Z,
        labelCell: i,
        labelPlane: null,
      })
    })
  }

  /**
   * Draw every spine label into ONE shared atlas canvas; nearby books later
   * get a small plane UV-sliced to their cell (buildLabelPlane). Guarded: a
   * headless/2D-canvas-less environment simply skips labels.
   */
  private buildLabelAtlas(placements: readonly BookPlacement[]): void {
    try {
      const cols = LABEL_ATLAS_COLS
      const rows = Math.max(1, Math.ceil(placements.length / cols))
      const canvas = document.createElement('canvas')
      canvas.width = cols * LABEL_CELL_W
      canvas.height = rows * LABEL_CELL_H
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.fillStyle = '#161310'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.font = '600 26px ui-monospace, monospace'
      ctx.textBaseline = 'middle'
      placements.forEach((p, i) => {
        const col = i % cols
        const row = Math.floor(i / cols)
        const [r, g, b] = spineColor(p.entityKind)
        ctx.fillStyle = `rgb(${Math.round(r * 255)},${Math.round(g * 255)},${Math.round(b * 255)})`
        ctx.fillRect(col * LABEL_CELL_W, row * LABEL_CELL_H, LABEL_CELL_W, LABEL_CELL_H)
        ctx.fillStyle = '#f5ebe0'
        const text = p.title.length > 26 ? p.title.slice(0, 25) + '…' : p.title
        ctx.fillText(text, col * LABEL_CELL_W + 8, row * LABEL_CELL_H + LABEL_CELL_H / 2)
      })
      const tex = new pc.Texture(this.app.graphicsDevice, { width: canvas.width, height: canvas.height })
      tex.setSource(canvas)
      this.labelAtlas = tex
    } catch {
      this.labelAtlas = null
    }
  }

  private buildThreads(placements: readonly BookPlacement[]): void {
    const pos = new Map(placements.map((p) => [p.id, p]))
    const edges = renderedGraphEdges(this.entries, new Set(pos.keys()))
    for (const edge of edges) {
      const a = pos.get(edge.fromId)
      const b = pos.get(edge.toId)
      if (!a || !b) continue
      const mat = new pc.StandardMaterial()
      mat.diffuse = new pc.Color(0.86, 0.43, 0.16)
      mat.emissive = new pc.Color(0.72, 0.28, 0.08)
      mat.emissiveIntensity = 0.4
      mat.useLighting = false
      mat.blendType = pc.BLEND_NORMAL
      const baseOpacity = edgeOpacity(edge.confidence)
      mat.opacity = baseOpacity
      mat.update()

      const e = new pc.Entity(`lib-thread-${edge.fromId}-${edge.toId}`)
      e.addComponent('render', { type: 'box' })
      orientSegment(e, [a.x, a.y, a.z], [b.x, b.y, b.z], 0.02)
      const mi = e.render?.meshInstances[0]
      if (mi) mi.material = mat
      this.contentRoot.addChild(e)
      this.threads.push({ entity: e, material: mat, fromId: edge.fromId, toId: edge.toId, baseOpacity })
    }
  }

  private buildMapGraph(placedIds: ReadonlySet<string>): void {
    const shown = this.entries.filter((e) => placedIds.has(e.id))
    const pins = mapPinPositions(shown)
    const pinPos = new Map(pins.map((p) => [p.id, p]))

    const pinMat = new pc.StandardMaterial()
    pinMat.diffuse = new pc.Color(0.95, 0.85, 0.4)
    pinMat.emissive = new pc.Color(0.6, 0.5, 0.15)
    pinMat.emissiveIntensity = 0.5
    pinMat.update()

    for (const pin of pins) {
      const e = new pc.Entity(`lib-pin-${pin.id}`)
      e.addComponent('render', { type: 'sphere' })
      e.setLocalPosition(pin.x, pin.y, pin.z)
      e.setLocalScale(0.1, 0.1, 0.1)
      const mi = e.render?.meshInstances[0]
      if (mi) mi.material = pinMat
      this.contentRoot.addChild(e)
      this.extras.push(e)
    }

    const edges = renderedGraphEdges(shown, new Set(pinPos.keys()))
    for (const edge of edges) {
      const a = pinPos.get(edge.fromId)
      const b = pinPos.get(edge.toId)
      if (!a || !b) continue
      const mat = new pc.StandardMaterial()
      mat.diffuse = new pc.Color(0.86, 0.43, 0.16)
      mat.emissive = new pc.Color(0.72, 0.28, 0.08)
      mat.emissiveIntensity = 0.4
      mat.useLighting = false
      mat.blendType = pc.BLEND_NORMAL
      mat.opacity = edgeOpacity(edge.confidence)
      mat.update()
      const e = new pc.Entity(`lib-mapthread-${edge.fromId}-${edge.toId}`)
      e.addComponent('render', { type: 'box' })
      orientSegment(e, [a.x, a.y, a.z], [b.x, b.y, b.z], 0.012)
      const mi = e.render?.meshInstances[0]
      if (mi) mi.material = mat
      this.contentRoot.addChild(e)
      // Map threads share the thread list so highlighting covers both views.
      this.threads.push({ entity: e, material: mat, fromId: edge.fromId, toId: edge.toId, baseOpacity: mat.opacity })
    }
  }

  // ── Proximity, [E] prompt, labels, selection ──────────────────────────────

  private tmpVec = new pc.Vec3()

  private updateProximity(): void {
    const camPos = this.cameraEntity.getPosition()

    // Rank books by distance for label texturing + [E] target.
    let nearestId: string | null = null
    let nearestDist = Infinity
    const ranked = this.books
      .map((b) => {
        const dx = camPos.x - b.worldX
        const dz = camPos.z - b.worldZ
        return { book: b, dist: Math.sqrt(dx * dx + dz * dz) }
      })
      .sort((a, b) => a.dist - b.dist)

    for (let i = 0; i < ranked.length; i++) {
      const { book, dist } = ranked[i]
      const wantLabel = i < MAX_LABELLED_BOOKS && this.labelAtlas
      if (wantLabel && !book.labelPlane) {
        book.labelPlane = this.buildLabelPlane(book)
      } else if (!wantLabel && book.labelPlane) {
        book.labelPlane.destroy()
        book.labelPlane = null
      }
      if (dist < nearestDist) {
        nearestDist = dist
        nearestId = book.placement.id
      }
    }

    this.currentInteractId = nearestDist <= INTERACT_DIST ? nearestId : null

    this.renderCard()
  }

  private buildLabelPlane(book: BookEntity): pc.Entity | null {
    if (!this.labelAtlas) return null
    const cols = LABEL_ATLAS_COLS
    const rows = Math.max(1, Math.ceil(this.books.length / cols))
    const col = book.labelCell % cols
    const row = Math.floor(book.labelCell / cols)

    let mat = this.labelMats.get(book.labelCell)
    if (!mat) {
      mat = new pc.StandardMaterial()
      mat.diffuseMap = this.labelAtlas
      mat.emissiveMap = this.labelAtlas
      mat.emissive = new pc.Color(1, 1, 1)
      mat.emissiveIntensity = 0.5
      mat.useLighting = true
      mat.diffuseMapTiling = new pc.Vec2(1 / cols, 1 / rows)
      mat.diffuseMapOffset = new pc.Vec2(col / cols, 1 - (row + 1) / rows)
      mat.emissiveMapTiling = new pc.Vec2(1 / cols, 1 / rows)
      mat.emissiveMapOffset = new pc.Vec2(col / cols, 1 - (row + 1) / rows)
      mat.update()
      this.labelMats.set(book.labelCell, mat)
    }

    const p = book.placement
    const e = new pc.Entity(`lib-book-label-${p.id}`)
    e.addComponent('render', { type: 'plane' })
    // Face outward from the aisle. A plane faces +Y at identity; rotate so it
    // stands vertical facing ±X.
    e.setLocalPosition(p.x + p.side * (BOOK_DEPTH_X / 2 + 0.005), p.y, p.z)
    e.setLocalEulerAngles(90, p.side === 1 ? 90 : -90, 0)
    e.setLocalScale(BOOK_WIDTH_Z * 0.95, 1, BOOK_HEIGHT * 0.95)
    const mi = e.render?.meshInstances[0]
    if (mi) mi.material = mat
    this.contentRoot.addChild(e)
    return e
  }

  private select(id: string): void {
    this.selectedId = id
    for (const t of this.threads) {
      const on = t.fromId === id || t.toId === id
      t.material.opacity = on ? 1 : t.baseOpacity * 0.5
      t.material.emissiveIntensity = on ? 1.2 : 0.4
      t.material.update()
    }
  }

  private renderCard(): void {
    const camComp = this.cameraEntity.camera
    if (!camComp) {
      this.card.style.display = 'none'
      return
    }

    // The detail card follows the SELECTED book; otherwise show the [E] prompt
    // over the nearest book.
    const focusId = this.selectedId ?? this.currentInteractId
    if (!focusId) {
      this.card.style.display = 'none'
      return
    }
    const book = this.books.find((b) => b.placement.id === focusId)
    const entry = this.entries.find((e) => e.id === focusId)
    if (!book || !entry) {
      this.card.style.display = 'none'
      return
    }

    const screen = computeLabelScreenPos(
      book.worldX, book.worldY + 0.5, book.worldZ,
      (wv, out) => camComp.worldToScreen(wv, out),
      this.tmpVec,
    )
    if (!screen) {
      this.card.style.display = 'none'
      return
    }

    this.card.style.display = 'block'
    this.card.style.left = `${screen.x}px`
    this.card.style.top = `${screen.y}px`
    this.card.style.transform = 'translate(-50%, -100%)'

    if (this.selectedId === focusId) {
      const loaded = new Set(this.entries.map((e) => e.id))
      const links = entry.relationships.filter((r) => loaded.has(r.to_id) && r.to_id !== entry.id)
      let html = `<div class="wcard">`
      html += `<div class="wcard-title">${escapeWorldHtml(entry.title)}</div>`
      html += `<div class="wcard-meta"><span class="wcard-pill">${escapeWorldHtml(entry.entityKind)}</span>`
      html += `<span>${links.length} link${links.length === 1 ? '' : 's'}</span></div>`
      for (const r of links.slice(0, 6)) {
        const to = this.entries.find((e) => e.id === r.to_id)
        html += `<div class="wcard-term"><div>&#8594; ${escapeWorldHtml(r.type)}: ${escapeWorldHtml(to?.title ?? r.to_id)}</div></div>`
      }
      html += `</div>`
      this.card.innerHTML = html
    } else {
      this.card.innerHTML =
        `<div class="wcard"><div class="wcard-title">${escapeWorldHtml(book.placement.title)}</div>` +
        `<div class="wcard-kbd-row"><span class="wcard-kbd">E</span> Read</div></div>`
    }
  }

  // ── Teardown ──────────────────────────────────────────────────────────────

  private clearContent(): void {
    for (const b of this.books) {
      b.labelPlane?.destroy()
      b.entity.destroy()
    }
    this.books = []
    for (const t of this.threads) t.entity.destroy()
    this.threads = []
    for (const e of this.extras) e.destroy()
    this.extras = []
    for (const mat of this.labelMats.values()) mat.destroy()
    this.labelMats.clear()
    this.labelAtlas?.destroy()
    this.labelAtlas = null
    this.selectedId = null
    this.currentInteractId = null
  }

  destroy(): void {
    this.disposeInput?.()
    this.disposeInput = undefined
    this.unsubscribe?.()
    this.unsubscribe = undefined
    this.clearContent()
    this.indexStatusTexture?.destroy()
    this.indexStatusTexture = null
    this.indexStatusMaterial = null
    this.indexStatusEntity = null
    this.overlay?.dispose()
    this.overlay = null
  }
}

// ── Segment orientation (pure enough; operates on an entity) ────────────────

/** Orient `e` (a unit box) so its local Z spans p→q, thickness on X/Y. */
export function orientSegment(
  e: pc.Entity,
  p: [number, number, number],
  q: [number, number, number],
  thickness: number,
): void {
  const dx = q[0] - p[0]
  const dy = q[1] - p[1]
  const dz = q[2] - p[2]
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz) || 0.001
  const yaw = (Math.atan2(dx, dz) * 180) / Math.PI
  const pitch = (-Math.asin(dy / len) * 180) / Math.PI
  e.setLocalPosition((p[0] + q[0]) / 2, (p[1] + q[1]) / 2, (p[2] + q[2]) / 2)
  e.setLocalEulerAngles(pitch, yaw, 0)
  e.setLocalScale(thickness, thickness, len)
}
